import Alpine from "alpinejs";

import { session } from "@app/lib/session";
import { POPOVER_EVENTS } from "@app/lib/events";

type PopoverConfig = {
  isOpen?: boolean;
  isDismissed?: boolean;
  openAt?: number;
};

Alpine.data("popover", (position: string, config?: PopoverConfig) => ({
  isOpen: config?.isOpen ?? false,
  isDismissed: config?.isDismissed ?? false,
  openAt: config?.openAt ?? 0,
  init() {
    Alpine.bind(this.$root, this.root);

    if (!this.$root.id) return;

    const dismissedPopups = session?.dismissedPopups?.value ?? [];
    const hasBeenDismissed = dismissedPopups.includes(this.$root.id);

    if (this.openAt > 0 && !hasBeenDismissed) {
      setTimeout(() => {
        this.handleOpen();
      }, this.openAt * 1000);
    }

    if (hasBeenDismissed) this.isDismissed = true;
    else this.impressionPopoverEvent();
  },

  hasBeenDismissed() {
    const dismissedPopups = session?.dismissedPopups?.value ?? [];
    return dismissedPopups.includes(this.$root.id);
  },

  // State Modifiers
  handleOpen() {
    this.impressionPopoverEvent();

    this.isOpen = true;
  },

  handleClose() {
    this.isOpen = false;
  },

  handleDimiss() {
    if (!this.$root.id) return;
    if (!this.isOpen) {
      this.handleOpen();
      return;
    }
    const dismissedPopups = session.dismissedPopups.value.split(",");
    session.dismissedPopups.value = [...dismissedPopups, this.$root.id].join(",");
    this.isDismissed = true;
    this.dismissPopoverEvent();
  },

  impressionPopoverEvent() {
    if (this.isDismissed) return;
    document.dispatchEvent(
      new CustomEvent(POPOVER_EVENTS.EVENT, {
        detail: {
          ele: this.$root,
          action: "impression",
        },
      }),
    );
  },

  dismissPopoverEvent() {
    document.dispatchEvent(
      new CustomEvent(POPOVER_EVENTS.EVENT, {
        detail: {
          ele: this.$el,
          action: "dismissed",
        },
      }),
    );
  },

  handleToggle() {
    if (this.isOpen) this.handleClose();
    else this.handleOpen();
  },

  // Bound Directives
  root: {
    ["@click"]() {
      if (this.isDismissed) return;
      this.$dispatch(POPOVER_EVENTS.EVENT, {
        ele: this.$el,
        action: "click",
      });
    },
    [":data-ui"]() {
      return !this.isOpen && "hidden";
    },
  },

  dismissable: {
    ["x-show"]() {
      if (this.hasBeenDismissed()) return false;
      if (!this.isOpen) return true;
      return this.isOpen && !this.isDismissed;
    },
  },

  parent: {
    ["x-ref"]: "parent",
  },

  child: {
    ["x-ref"]: "child",
    [`x-anchor.${position}`]: "$refs.parent",
    ["x-show"]() {
      return this.isOpen;
    },
  },

  toggle: {
    ["@click"]() {
      this.handleToggle();
    },
  },

  dismiss: {
    ["@click"]() {
      this.handleDimiss();
    },
  },
  force_dismiss: {
    ["@click"]() {
      const dismissedPopups = session.dismissedPopups.value.split(",");
      session.dismissedPopups.value = [...dismissedPopups, this.$root.id].join(",");
      this.isOpen = true;
      this.isDismissed = true;
    },
  },

  content: {
    ["@click.outside"]() {
      this.handleClose();
    },
  },
}));
